import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRosterTemplatesCatalog } from 'hooks/incident/useRoster';
import {
  partitionBySource,
  partitionRosterTemplates,
} from 'components/IncidentConfig/roster/templates/rosterTemplateCatalog';
import type { RosterTemplateCatalogEntry } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';
import type { RosterTemplateLibraryEntry } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';

import {
  cloneTemplateAsDraft,
  type TemplateEditorSeed,
} from './rosterTemplateSeats';

const BLANK_SEED: TemplateEditorSeed = {
  name: '',
  description: null,
  attachOnly: false,
  nodes: [],
};

const BLANK_START = '__blank__';

export type StartFromChoice = {
  seed: TemplateEditorSeed;
  sourceTemplateId: string | null;
  sourceSlug: string | null;
};

export function StartFromDialog({
  open,
  onClose,
  onChoose,
  title = 'New roster template',
  description = 'Start from a blank chart, or clone an existing template — including the built-in ICS ones — and edit it on the org chart.',
  allowBlank = true,
  libraryTemplates = [],
}: {
  open: boolean;
  onClose: () => void;
  onChoose: (choice: StartFromChoice) => void;
  title?: string;
  description?: string;
  allowBlank?: boolean;
  libraryTemplates?: RosterTemplateLibraryEntry[];
}) {
  const catalogQuery = useRosterTemplatesCatalog();

  const { organization, builtInTemplates, builtInExpansions } = useMemo(() => {
    const entries = catalogQuery.data?.templates ?? [];
    const { templates, expansions } = partitionRosterTemplates(entries);
    const templateSources = partitionBySource(templates);
    const expansionSources = partitionBySource(expansions);
    return {
      organization: [
        ...templateSources.organization,
        ...expansionSources.organization,
      ],
      builtInTemplates: templateSources.builtIn,
      builtInExpansions: expansionSources.builtIn,
    };
  }, [catalogQuery.data?.templates]);

  const defaultStart = allowBlank
    ? BLANK_START
    : libraryTemplates[0]
      ? `library:${libraryTemplates[0].id}`
      : (organization[0]?.slug ??
        builtInTemplates[0]?.slug ??
        builtInExpansions[0]?.slug ??
        BLANK_START);
  const [startFrom, setStartFrom] = useState(defaultStart);

  useEffect(() => {
    if (open) setStartFrom(defaultStart);
  }, [defaultStart, open]);

  const libraryChosen = useMemo(() => {
    if (!startFrom.startsWith('library:')) return null;
    const id = startFrom.slice('library:'.length);
    return libraryTemplates.find((entry) => entry.id === id) ?? null;
  }, [libraryTemplates, startFrom]);

  const catalogChosen = useMemo(() => {
    if (startFrom === BLANK_START || startFrom.startsWith('library:'))
      return null;
    return (
      organization.find((entry) => entry.slug === startFrom) ??
      builtInTemplates.find((entry) => entry.slug === startFrom) ??
      builtInExpansions.find((entry) => entry.slug === startFrom) ??
      null
    );
  }, [builtInExpansions, builtInTemplates, organization, startFrom]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-2">
          <Label htmlFor="roster-template-start-from">Start from</Label>
          <Select
            value={startFrom}
            onValueChange={(value) => {
              if (typeof value === 'string') setStartFrom(value);
            }}
          >
            <SelectTrigger id="roster-template-start-from">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark">
              {allowBlank ? (
                <SelectItem value={BLANK_START}>Blank chart</SelectItem>
              ) : null}
              {libraryTemplates.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>Organization library</SelectLabel>
                  {libraryTemplates.map((entry) => (
                    <SelectItem key={entry.id} value={`library:${entry.id}`}>
                      {entry.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
              {organization.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>Organization</SelectLabel>
                  {organization.map((entry) => (
                    <SelectItem key={entry.slug} value={entry.slug}>
                      {sourceLabel(entry)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
              {builtInTemplates.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>Built-in templates</SelectLabel>
                  {builtInTemplates.map((entry) => (
                    <SelectItem key={entry.slug} value={entry.slug}>
                      {sourceLabel(entry)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
              {builtInExpansions.length > 0 ? (
                <SelectGroup>
                  <SelectLabel>Built-in expansions</SelectLabel>
                  {builtInExpansions.map((entry) => (
                    <SelectItem key={entry.slug} value={entry.slug}>
                      {sourceLabel(entry)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!allowBlank && !libraryChosen && !catalogChosen}
            onClick={() => {
              if (libraryChosen) {
                onChoose({
                  seed: cloneTemplateAsDraft({
                    name: libraryChosen.name,
                    description: libraryChosen.description,
                    attachOnly: libraryChosen.attachOnly,
                    nodes: libraryChosen.nodes,
                  }),
                  sourceTemplateId: libraryChosen.id,
                  sourceSlug: null,
                });
                return;
              }
              if (catalogChosen) {
                onChoose({
                  seed: cloneTemplateAsDraft({
                    name: catalogChosen.name,
                    description: catalogChosen.description,
                    attachOnly: catalogChosen.attachOnly,
                    nodes: catalogChosen.nodes ?? [],
                  }),
                  sourceTemplateId: null,
                  sourceSlug: catalogChosen.slug,
                });
                return;
              }
              onChoose({
                seed: BLANK_SEED,
                sourceTemplateId: null,
                sourceSlug: null,
              });
            }}
          >
            Open editor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function sourceLabel(entry: RosterTemplateCatalogEntry): string {
  return entry.attachOnly ? `${entry.name} (expansion)` : entry.name;
}
